/**
 * DrawerHeaderBlock — the drawer's header band (UX-11 PR-2), extracted
 * from WorkItemDrawer: close affordance, stage label + required-leaf
 * progress chip, the inline-editable canonical name (clients.display_name
 * once converted, else prospect_name — pre-conversion edit only, #1;
 * post-conversion it lives on the client record, r1 #5), the Opened
 * line, unit·service parity row, and the "Next:" stage callout (the
 * checkpoint pointer; the action stays the checklist's "Done · advance").
 *
 * editingName state moves with the block. The close/name-edit buttons
 * keep their icon-glyph forms (an icon-only ✕/✎ the Button variants
 * don't express) — this file carries a conscious raw-button allowlist
 * entry.
 *
 * Design pass 2026-07-25 (soak r2), three things:
 *   - the header is PINNED (the drawer body scrolls beneath it), so the
 *     name, the stage and the count stay on screen while you work the
 *     checklist. It sits on paper against the cream body, which is what
 *     separates chrome from content without a heavier rule.
 *   - ONE meta line. "Opened Jun 1" and "unit · service" were two
 *     11-12px lines stacked directly under the title, on two baselines,
 *     saying the same KIND of thing. They are now one line.
 *   - radius comes from the scale (rounded-base), not a bespoke
 *     rounded-[10px] (DL5).
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CLIENT_BUBBLE_CLASS } from '@/components/workspace/ClientBubble';
import { X } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canOpenEveryClientRecord } from '@/lib/capabilities';
import { TextInput } from '@/components/primitives/TextInput';
import { displayValue } from '@/lib/valueCase';
import type { AssignUnitWire, UpdateWorkItemInput, WorkItemDetail, PipelineStageWire } from '@/lib/api';
import {
  isOverdue,
  shortDate,
  stageCountClass,
  unitDotColor,
} from '@/components/workspace/workspaceFormat';
import { SelectField } from '@/components/primitives/SelectField';

export interface DrawerHeaderBlockProps {
  detail: WorkItemDetail | undefined;
  canManage: boolean;
  onClose: () => void;
  stageLabel: string | null;
  stageProgress: { done: number; total: number };
  showAdvance: boolean;
  nextStage: PipelineStageWire | null;
  onEdit: (edits: UpdateWorkItemInput) => void;
  /** The unit catalog, for the header's own Unit picker (intake only). Empty
   *  for a viewer who can't manage the card — the picker never renders then. */
  units?: AssignUnitWire[];
}

export function DrawerHeaderBlock({
  detail,
  canManage,
  onClose,
  stageLabel,
  stageProgress,
  showAdvance,
  nextStage,
  onEdit,
  units = [],
}: DrawerHeaderBlockProps): ReactNode {
  const [editingName, setEditingName] = useState(false);
  // TS-6a — post-client-setup, the drawer links through to the client
  // record. SOAK SCOPING (founder, 2026-07-06): the link affordance shows
  // only for view_all_clinical holders (the record page's soak gate); line
  // CMs join at CM rollout — the capability floor (read_clinical_record,
  // all six roles) unchanged, SURFACE gate only.
  const { tenantSlug, whoami } = useTenant();
  const showRecordLink = canOpenEveryClientRecord(whoami);

  return (
    <div className="shrink-0 border-b border-sage-200 bg-paper px-5 pt-4 pb-3">
      <button type="button" onClick={onClose} aria-label="Close" data-testid="drawer-close" className="float-right text-sage-400 text-xl leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
        <X size={18} aria-hidden="true" />
      </button>
      {detail && (
        <div className="text-[10.5px] font-semibold uppercase tracking-wider mb-1.5">
          <span className="text-sage-400">{stageLabel ?? detail.kind}</span>
          {stageProgress.total > 0 && (
            <span data-testid="drawer-stage-progress"
              className={stageCountClass(stageProgress.done, stageProgress.total, isOverdue(detail))}>
              {' · '}{stageProgress.done}/{stageProgress.total}
            </span>
          )}
        </div>
      )}
      {/* Slice 3b-2: canonical name (clients.display_name once converted, else
          prospect_name). Inline-editable pre-conversion (#1); post-conversion it
          lives on the client record (r1 #5 — defer that edit). */}
      {editingName && detail && canManage && !detail.client_id ? (
        // A task's name IS its `title`; a pre-conversion intake's name is `prospect_name`
        // (they are distinct columns and the repo guards prospect_name as intake-only, so a
        // task edit MUST PATCH `title` — sending prospect_name on a task is rejected and the
        // rename is silently lost). Branch on kind so the right column is written.
        <TextInput autoFocus data-testid="drawer-name-input" nameCase={detail.kind === 'intake' ? 'person' : undefined}
          defaultValue={detail.kind !== 'intake' ? (detail.title ?? '') : (detail.prospect_name ?? detail.display_name ?? '')}
          onBlur={(e) => { const v = e.target.value.trim(); const prev = detail.kind !== 'intake' ? (detail.title ?? '') : (detail.prospect_name ?? ''); if (v && v !== prev) onEdit(detail.kind !== 'intake' ? { title: v } : { prospect_name: v }); setEditingName(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); else if (e.key === 'Escape') setEditingName(false); }}
          className="font-display text-2xl font-semibold text-sage-800 w-full border-b border-sage-300 bg-transparent focus:outline-none" />
      ) : (
        <h2 className="font-display text-2xl font-semibold text-sage-800 m-0 flex items-center gap-2 flex-wrap">
          <span data-testid="drawer-name">{detail?.display_name ?? detail?.title ?? 'Work item'}</span>
          {detail && canManage && !detail.client_id && (
            <button type="button" data-testid="drawer-name-edit" aria-label="Edit name" onClick={() => setEditingName(true)}
              className="text-sage-400 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">✎</button>
          )}
          {detail?.client_id && (showRecordLink ? (
            // LP-43: the client bubble the task rows wear. A task names its
            // client; an intake's heading already is the client's name.
            <Link
              to={`/t/${tenantSlug}/clients/${detail.client_id}`}
              data-testid="drawer-open-client-record"
              className={`max-w-full truncate font-body text-sm ${CLIENT_BUBBLE_CLASS}`}
            >
              {detail.kind === 'task' && detail.client_display_name ? detail.client_display_name : 'Open client record'}
            </Link>
          ) : (
            <span className="text-[11px] text-sage-400 font-normal" data-testid="drawer-name-converted">· edit on the client record</span>
          ))}
        </h2>
      )}
      {/* ONE meta line: unit (color dot + name) · service · opened. Block E
          header parity kept; the "Opened" line folded in beside it.
          The unit is otherwise only settable deep in the Ready for
          Assignment checklist section (StageChecklist, WorkItemDrawer): a
          manager gets it right here too, beside the stage, on an intake —
          added or changed, same manage_work_items gate and PATCH route as
          that picker. A viewer who can't manage the card keeps the plain
          dot-and-name text, and gets nothing at all pre-assignment (same
          as before) rather than an inert control. */}
      {detail && (() => {
        const editableUnit = canManage && detail.kind === 'intake';
        const hasUnitSegment = editableUnit || !!detail.unit_name;
        const hasServiceSegment = !!detail.service_requested;
        return (
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] text-ink-soft">
            {(hasUnitSegment || hasServiceSegment) && (
              <>
                <span className="inline-flex items-center gap-1.5" data-testid="drawer-unit-service">
                  {hasUnitSegment && (
                    <>
                      <span aria-hidden="true" className="inline-block w-2 h-2 rounded-pill flex-shrink-0" style={{ backgroundColor: unitDotColor(detail.unit_id, detail.unit_color) ?? 'var(--color-sage-300)' }} />
                      {editableUnit ? (
                        <SelectField size="compact" quiet
                          value={detail.unit_id ?? ''}
                          aria-label="Unit"
                          data-testid="drawer-header-unit-select"
                          onChange={(e) => onEdit({ unit_id: e.target.value || null })}
                        >
                          <option value="">No unit</option>
                          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </SelectField>
                      ) : (
                        <span>{detail.unit_name}</span>
                      )}
                    </>
                  )}
                  {hasUnitSegment && hasServiceSegment && <span className="text-sage-300">·</span>}
                  {detail.service_requested && <span>{displayValue(detail.service_requested)}</span>}
                </span>
                <span aria-hidden="true" className="text-sage-300">·</span>
              </>
            )}
            <span className="italic text-sage-400" data-testid="drawer-opened">Opened {shortDate(detail.created_at)}</span>
          </div>
        );
      })()}
      {/* Slice 3b eyeball #2: "Next" points to the next STAGE (the checkpoint),
          not a granular sub-item. The action is the checklist's "Done · advance". */}
      {detail && showAdvance && nextStage && (
        <div className="flex items-center gap-2.5 mt-3 bg-warm-50 border border-warm-200 rounded-base px-3 py-2">
          <span className="w-[7px] h-[7px] rounded-pill bg-warm-400 flex-shrink-0" aria-hidden="true" />
          <div className="text-[13px]" data-testid="drawer-next-stage"><span className="text-sage-400">Next:</span> <span className="font-semibold text-sage-800">{nextStage.label}</span></div>
        </div>
      )}
    </div>
  );
}
